"use client";

import { c as _c } from "react/compiler-runtime";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ProviderScheduleTable, ProviderSchedule } from "@/src/components/tasks/provider-schedule-table";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { Referral } from "@/src/models/documents/referral";
import { getFullAddress } from "@/src/models/patient";

// Define the location type
export interface LocationWithDistance {
  name: string;
  address: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  distance: number;
  scheduleName: string;
}
interface USHVReferralInstructionsProps {
  referral: Referral;
  sortedLocations: LocationWithDistance[];
  schedules: ProviderSchedule[];
  receivingProvider: string | null;
  receivingLocation: string | null;
  receivingProviderOptions: string[];
  receivingLocationOptions: string[];
  nameToScheduleNameMap: Map<string, string>;
}
function customReferralString(referral: Referral): string {
  const missingFields = [];
  if (!referral.diagnosisList?.length) {
    missingFields.push("diagnosisText");
  }
  if (!referral.referringProvider?.familyName) {
    missingFields.push("referringProvider");
  }
  if (missingFields.length > 0) {
    return `referral missing:\n${missingFields.join("\n")}`;
  }
  const diagnosisText = referral.diagnosisList?.map((d: {
    diagnosisText: string | null;
  }) => d.diagnosisText).filter(Boolean).join("; ");
  return `referral from ${referral.referringProvider!.familyName} dx: ${diagnosisText}`;
}
export function USHVReferralInstructions(t0) {
  const $ = _c(44);
  const {
    referral,
    sortedLocations,
    receivingProvider,
    receivingLocation,
    schedules,
    receivingProviderOptions,
    receivingLocationOptions,
    nameToScheduleNameMap
  } = t0;
  const receivingProviderName = receivingProvider ? receivingProvider : "ANY";
  const receivingLocationName = receivingLocation ? receivingLocation : "ANY";
  let t1;
  if ($[0] !== referral) {
    t1 = customReferralString(referral);
    $[0] = referral;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referralMessage = t1;
  let t2;
  if ($[2] !== referral.patient) {
    t2 = getFullAddress(referral.patient);
    $[2] = referral.patient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const patientAddress = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="font-medium">Receiving Provider:</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== receivingProviderName) {
    t4 = <p>{t3}{" "}<span className="text-muted-foreground"><CopyableContent text={receivingProviderName} /></span></p>;
    $[5] = receivingProviderName;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== sortedLocations) {
    t5 = sortedLocations && sortedLocations.length > 0 ? <><p className="font-medium">Closest Three Locations to Patient:</p><div className="space-y-1">{sortedLocations.map(_temp)}</div></> : <p className="text-muted-foreground">No nearby locations found</p>;
    $[7] = sortedLocations;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="font-medium">Referral Message:</span>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== referralMessage) {
    t7 = <p>{t6}{" "}<span className="text-muted-foreground"><CopyableContent text={referralMessage} /></span></p>;
    $[10] = referralMessage;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="font-medium">Patient Address:</span>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = patientAddress || "N/A";
  let t10;
  if ($[13] !== t9) {
    t10 = <p>{t8}{" "}<span className="text-muted-foreground"><CopyableContent text={t9} /></span></p>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== nameToScheduleNameMap || $[16] !== receivingLocationName || $[17] !== receivingLocationOptions || $[18] !== receivingProviderName || $[19] !== receivingProviderOptions || $[20] !== schedules) {
    t11 = <div className="mt-4"><ProviderScheduleTable schedules={schedules} receivingLocationName={receivingLocationName} receivingProviderName={receivingProviderName} receivingProviderOptions={receivingProviderOptions} receivingLocationOptions={receivingLocationOptions} nameToScheduleNameMap={nameToScheduleNameMap} /></div>;
    $[15] = nameToScheduleNameMap;
    $[16] = receivingLocationName;
    $[17] = receivingLocationOptions;
    $[18] = receivingProviderName;
    $[19] = receivingProviderOptions;
    $[20] = schedules;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t4 || $[25] !== t5 || $[26] !== t7) {
    t12 = <EhrInstructionsCard><div className="text-sm text-muted-foreground"><div className="flex flex-col gap-2">{t4}{t5}{t7}{t10}{t11}</div></div></EhrInstructionsCard>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t4;
    $[25] = t5;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== referral.patient) {
    t13 = <PatientCard patient={referral.patient} />;
    $[28] = referral.patient;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  let t15;
  if ($[30] !== referral) {
    t14 = <ReferralProviderCards referral={referral} />;
    t15 = <ReferralCard referral={referral} isEditable={true} />;
    $[30] = referral;
    $[31] = t14;
    $[32] = t15;
  } else {
    t14 = $[31];
    t15 = $[32];
  }
  let t16;
  if ($[33] !== referral.primaryCoverage) {
    t16 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[33] = referral.primaryCoverage;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== referral.secondaryCoverage) {
    t17 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[35] = referral.secondaryCoverage;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t12 || $[38] !== t13 || $[39] !== t14 || $[40] !== t15 || $[41] !== t16 || $[42] !== t17) {
    t18 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t12}{t13}{t14}{t15}{t16}{t17}</div>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
}
function _temp(location, index) {
  return <div key={location.name}><p className="font-medium">{index + 1}. {location.name} -{" "}<span className="font-normal text-muted-foreground">{location.address}</span>{" "}<span className="text-xs">(~{location.distance.toFixed(1)} miles)</span></p></div>;
}